import {
  Stack,
  Title,
  Paper,
  Text,
  Table,
  Textarea,
  Group,
  Button,
  Badge,
  Loader,
  Center,
  Alert,
  ActionIcon,
  Select,
  Anchor,
} from "@mantine/core";
import "@mantine/core/styles.css";
import { DatePickerInput } from "@mantine/dates";
import { useCallback, useEffect, useMemo, useState } from "react";
import { notifications } from "@mantine/notifications";
import {
  IconInfoCircle,
  IconPlus,
  IconMinus,
  IconArrowLeft,
  IconTrash,
  IconEdit,
  IconRefresh,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import useTransientStore from "../../../data/transcientStore";
import {
  getMvrOverview,
  getMvrRound,
  createMvrRound,
  updateMvrCounter,
  markMvrRoundReady,
  releaseMvrRound,
  deleteMvrRound,
  getMvrNoticeOptions,
  saveMvrNotice,
  deleteMvrNotice,
  MvrOverview,
  MvrRoundDetail,
  MvrNotice,
  MvrNoticeOptions,
} from "../../../services/MvrService";

export interface MvrTabProps {
  form: any;
  addressSystemId?: number;
  userId?: number;
}

type View = "list" | "round" | "notice";

const toIsoDate = (value: unknown): string | null => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value as string);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const toDateValue = (value: string | null): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("fi-FI");
};

export default function MvrTab({
  form,
  addressSystemId,
  userId,
}: Readonly<MvrTabProps>) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 722,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });

  const { checkAccess } = usePermissions();
  const canModify = checkAccess("CITY_OBJECT_MVR");
  const canDelete = checkAccess("CITY_OBJECT_MVR_DELETE");
  const canRelease = checkAccess("CITY_OBJECT_MVR_RELEASE");

  const addrId =
    addressSystemId !== undefined && addressSystemId > 0
      ? addressSystemId
      : addrIdFromStore;

  const contractId: number | undefined = useMemo(() => {
    const explicit = form?.values?.contractId;
    if (typeof explicit === "number" && explicit > 0) return explicit;
    const job = form?.values?.job;
    if (typeof job === "string" && job.length > 0) {
      const parsed = parseInt(job, 10);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  }, [form?.values?.contractId, form?.values?.job]);

  const objectId: number | undefined = useMemo(() => {
    const explicit = form?.values?.objectId;
    if (typeof explicit === "number" && explicit > 0) return explicit;
    const objId = form?.values?.id;
    if (typeof objId === "number" && objId > 0) return objId;
    return undefined;
  }, [form?.values?.objectId, form?.values?.id]);

  const ready = !!contractId && !!objectId && !!addrId && !!userId;

  const [view, setView] = useState<View>("list");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [overview, setOverview] = useState<MvrOverview | null>(null);
  const [round, setRound] = useState<MvrRoundDetail | null>(null);

  // Notice editing state
  const [noticeGroupId, setNoticeGroupId] = useState<number | null>(null);
  const [noticeOptions, setNoticeOptions] = useState<MvrNoticeOptions | null>(
    null,
  );
  const [editingNotice, setEditingNotice] = useState<MvrNotice | null>(null);
  const [noticeItemId, setNoticeItemId] = useState<string | null>(null);
  const [noticeResponsible, setNoticeResponsible] = useState<string | null>(
    null,
  );
  const [noticeDescription, setNoticeDescription] = useState("");
  const [noticeDueDate, setNoticeDueDate] = useState<Date | null>(null);
  const [noticeFixed, setNoticeFixed] = useState<Date | null>(null);
  const [noticeFixedDescription, setNoticeFixedDescription] = useState("");

  const loadOverview = useCallback(() => {
    if (!ready) return;
    setLoading(true);
    getMvrOverview(objectId!, contractId!, userId!, addrId!)
      .then((d) => setOverview(d))
      .catch(() =>
        notifications.show({
          color: "red",
          message:
            getTranslation("MESSAGE_LOAD_FAILED") || "Failed to load MVR data",
        }),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, objectId, contractId, userId, addrId]);

  const loadRound = useCallback(
    (id: number) => {
      if (!ready) return;
      setLoading(true);
      getMvrRound(id, objectId!, contractId!, userId!, addrId!)
        .then((d) => setRound(d))
        .catch(() =>
          notifications.show({
            color: "red",
            message:
              getTranslation("MESSAGE_LOAD_FAILED") ||
              "Failed to load MVR data",
          }),
        )
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, objectId, contractId, userId, addrId],
  );

  useEffect(() => {
    if (view === "list") loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, contractId, objectId, addrId, userId]);

  const identity = () => ({
    objectId: objectId!,
    contractId: contractId!,
    userId: userId!,
    addressSystemId: addrId!,
  });

  const openRound = (id: number) => {
    setView("round");
    loadRound(id);
  };

  const handleAddNew = () => {
    if (!ready) return;
    setBusy(true);
    createMvrRound(identity())
      .then((id) => openRound(id))
      .catch(() =>
        notifications.show({
          color: "red",
          message:
            getTranslation("MESSAGE_SAVE_FAILED") || "Failed to create round",
        }),
      )
      .finally(() => setBusy(false));
  };

  const handleCounter = (
    groupId: number,
    counterType: number,
    change: number,
  ) => {
    if (!round) return;
    setBusy(true);
    updateMvrCounter({ ...identity(), id: round.id, groupId, counterType, change })
      .then(() => loadRound(round.id))
      .catch(() =>
        notifications.show({
          color: "red",
          message: getTranslation("MESSAGE_SAVE_FAILED") || "Update failed",
        }),
      )
      .finally(() => setBusy(false));
  };

  const handleMarkReady = () => {
    if (!round) return;
    const confirmText =
      getTranslation("CONFIRM_MARK_READY") ||
      "Are you sure you want to save this round as ready?";
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    markMvrRoundReady({ ...identity(), id: round.id })
      .then(() => {
        notifications.show({
          color: "green",
          message: getTranslation("MESSAGE_SAVED") || "Round saved",
        });
        setView("list");
      })
      .catch(() =>
        notifications.show({
          color: "red",
          message: getTranslation("MESSAGE_SAVE_FAILED") || "Save failed",
        }),
      )
      .finally(() => setBusy(false));
  };

  const handleDeleteRound = () => {
    if (!round) return;
    const confirmText =
      getTranslation("CONFIRM_DELETE_ROUND") ||
      "Are you sure you want to delete this round?";
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    deleteMvrRound({ ...identity(), id: round.id })
      .then(() => {
        notifications.show({
          color: "green",
          message: getTranslation("MESSAGE_DELETED") || "Round deleted",
        });
        setView("list");
      })
      .catch(() =>
        notifications.show({
          color: "red",
          message: getTranslation("MESSAGE_ACTION_FAILED") || "Delete failed",
        }),
      )
      .finally(() => setBusy(false));
  };

  const handleReleaseRound = (id: number) => {
    const confirmText =
      getTranslation("CONFIRM_RELEASE") ||
      "Are you sure you want to release this round for editing?";
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    releaseMvrRound({ ...identity(), id })
      .then(() => {
        notifications.show({
          color: "green",
          message: getTranslation("MESSAGE_RELEASED") || "Round released",
        });
        loadOverview();
      })
      .catch(() =>
        notifications.show({
          color: "red",
          message: getTranslation("MESSAGE_ACTION_FAILED") || "Release failed",
        }),
      )
      .finally(() => setBusy(false));
  };

  const openNotice = (groupId: number, notice: MvrNotice | null) => {
    if (!round) return;
    setBusy(true);
    getMvrNoticeOptions(groupId, objectId!, contractId!, userId!, addrId!)
      .then((opts) => {
        setNoticeOptions(opts);
        setNoticeGroupId(groupId);
        setEditingNotice(notice);
        setNoticeItemId(notice ? String(notice.itemId) : null);
        setNoticeResponsible(
          notice?.responsibleUserId != null
            ? String(notice.responsibleUserId)
            : null,
        );
        setNoticeDescription(notice?.description ?? "");
        setNoticeDueDate(toDateValue(notice?.dueDate ?? null));
        setNoticeFixed(toDateValue(notice?.fixed ?? null));
        setNoticeFixedDescription(notice?.fixedDescription ?? "");
        setView("notice");
      })
      .catch(() =>
        notifications.show({
          color: "red",
          message: getTranslation("MESSAGE_LOAD_FAILED") || "Load failed",
        }),
      )
      .finally(() => setBusy(false));
  };

  const handleSaveNotice = () => {
    if (!round) return;
    if (!noticeItemId) {
      notifications.show({
        color: "red",
        message:
          getTranslation("ALERT_DEFECT_REQUIRED") ||
          "Please select a defect or error",
      });
      return;
    }
    setBusy(true);
    saveMvrNotice({
      ...identity(),
      id: editingNotice?.id ?? 0,
      mvrMeasurementId: round.id,
      mvrItemId: parseInt(noticeItemId, 10),
      responsibleUserId: noticeResponsible
        ? parseInt(noticeResponsible, 10)
        : null,
      dueDate: toIsoDate(noticeDueDate),
      description: noticeDescription || null,
      fixed: toIsoDate(noticeFixed),
      fixedDescription: noticeFixedDescription || null,
    })
      .then(() => {
        notifications.show({
          color: "green",
          message: getTranslation("MESSAGE_SAVED") || "Notice saved",
        });
        setView("round");
        loadRound(round.id);
      })
      .catch(() =>
        notifications.show({
          color: "red",
          message: getTranslation("MESSAGE_SAVE_FAILED") || "Save failed",
        }),
      )
      .finally(() => setBusy(false));
  };

  const handleDeleteNotice = (notice: MvrNotice) => {
    if (!round) return;
    const confirmText =
      getTranslation("CONFIRM_DELETE_NOTICE") ||
      "Are you sure you want to delete this notice?";
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    deleteMvrNotice({
      ...identity(),
      id: notice.id,
      mvrMeasurementId: round.id,
    })
      .then(() => loadRound(round.id))
      .catch(() =>
        notifications.show({
          color: "red",
          message: getTranslation("MESSAGE_ACTION_FAILED") || "Delete failed",
        }),
      )
      .finally(() => setBusy(false));
  };

  if (!ready) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        {getTranslation("MESSAGE_SELECT_CONTRACT_OBJECT") ||
          "Select a contract and object to view MVR measurements."}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  // ── Notice form view ───────────────────────────────────────────────────
  if (view === "notice" && noticeOptions) {
    const noticeEditable = !round || round.status < 2;
    return (
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4} c="blue">
            {noticeOptions.groupName}
          </Title>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => setView("round")}
          >
            {getTranslation("BUTTON_BACK") || "Back"}
          </Button>
        </Group>
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Select
              label={getTranslation("LABEL_DEFECT_OR_ERROR") || "Defect or error"}
              placeholder={getTranslation("LABEL_SELECT") || "Select"}
              required
              disabled={!noticeEditable}
              value={noticeItemId}
              onChange={setNoticeItemId}
              data={noticeOptions.items.map((i) => ({
                value: String(i.id),
                label: i.name ?? "",
              }))}
            />
            <Textarea
              label={getTranslation("LABEL_ADDITIONAL_INFO") || "Additional information"}
              autosize
              minRows={4}
              disabled={!noticeEditable}
              value={noticeDescription}
              onChange={(e) => setNoticeDescription(e.currentTarget.value)}
            />
            <Select
              label={getTranslation("LABEL_RESPONSIBLE_PERSON") || "Responsible person"}
              placeholder={getTranslation("LABEL_SELECT") || "Select"}
              disabled={!noticeEditable}
              clearable
              value={noticeResponsible}
              onChange={setNoticeResponsible}
              data={noticeOptions.responsibleUsers.map((u) => ({
                value: String(u.id),
                label: u.name ?? "",
              }))}
            />
            <DatePickerInput
              label={getTranslation("LABEL_DUE_DATE") || "Date to be corrected"}
              valueFormat="DD.MM.YYYY"
              clearable
              disabled={!noticeEditable}
              value={noticeDueDate}
              onChange={(value) =>
                setNoticeDueDate(value ? new Date(value as unknown as string) : null)
              }
            />
            <DatePickerInput
              label={getTranslation("LABEL_FIXED_DATE") || "Date corrected"}
              valueFormat="DD.MM.YYYY"
              clearable
              value={noticeFixed}
              onChange={(value) =>
                setNoticeFixed(value ? new Date(value as unknown as string) : null)
              }
            />
            <Textarea
              label={getTranslation("LABEL_FIXED_INFO") || "Correction information"}
              autosize
              minRows={3}
              value={noticeFixedDescription}
              onChange={(e) => setNoticeFixedDescription(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button onClick={handleSaveNotice} loading={busy} disabled={!canModify}>
                {getTranslation("BUTTON_SAVE") || "Save"}
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  // ── Round form view ────────────────────────────────────────────────────
  if (view === "round" && round) {
    const editable = round.status === 1 && canModify;
    return (
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4} c="blue">
            {getTranslation("TITLE_MVR_ROUND") || "MVR measurement"}
          </Title>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => setView("list")}
          >
            {getTranslation("BUTTON_BACK") || "Back"}
          </Button>
        </Group>

        <Paper withBorder p="md" radius="md">
          <Table verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{getTranslation("LABEL_GROUP") || "Group"}</Table.Th>
                <Table.Th style={{ width: 160 }}>
                  {getTranslation("LABEL_CORRECT") || "Correct"}
                </Table.Th>
                <Table.Th style={{ width: 160 }}>
                  {getTranslation("LABEL_INCORRECT") || "Incorrect"}
                </Table.Th>
                <Table.Th style={{ width: 140 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {round.groups.map((g) => (
                <Table.Tr key={g.groupId}>
                  <Table.Td>
                    <Text size="sm">{g.groupName}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <ActionIcon
                        variant="default"
                        disabled={!editable || busy}
                        onClick={() => handleCounter(g.groupId, 1, -1)}
                        aria-label="decrement-correct"
                      >
                        <IconMinus size={16} />
                      </ActionIcon>
                      <Text w={28} ta="center">
                        {g.correctCount}
                      </Text>
                      <ActionIcon
                        color="green"
                        variant="filled"
                        disabled={!editable || busy}
                        onClick={() => handleCounter(g.groupId, 1, 1)}
                        aria-label="increment-correct"
                      >
                        <IconPlus size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <ActionIcon
                        variant="default"
                        disabled={!editable || busy}
                        onClick={() => handleCounter(g.groupId, 2, -1)}
                        aria-label="decrement-incorrect"
                      >
                        <IconMinus size={16} />
                      </ActionIcon>
                      <Text w={28} ta="center">
                        {g.incorrectCount}
                      </Text>
                      <ActionIcon
                        color="red"
                        variant="filled"
                        disabled={!editable || busy}
                        onClick={() => handleCounter(g.groupId, 2, 1)}
                        aria-label="increment-incorrect"
                      >
                        <IconPlus size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      disabled={!editable}
                      onClick={() => openNotice(g.groupId, null)}
                    >
                      {getTranslation("BUTTON_ADD_NOTICE") || "Add notice"}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Group justify="space-between" mt="md">
            <Group gap="xs">
              <Badge color="green" size="lg">
                {round.totalCorrect} {getTranslation("LABEL_CORRECT") || "correct"}
              </Badge>
              <Badge color="red" size="lg">
                {round.totalIncorrect}{" "}
                {getTranslation("LABEL_INCORRECT") || "incorrect"}
              </Badge>
            </Group>
            <Text fw={600}>
              {getTranslation("LABEL_RESULT") || "Result"}: {round.totalCorrect}/
              {round.totalCorrect + round.totalIncorrect} = {round.percentage} %
            </Text>
          </Group>

          <Group justify="flex-end" mt="md">
            {canDelete && round.status === 1 && (
              <Button
                color="red"
                variant="light"
                leftSection={<IconTrash size={16} />}
                onClick={handleDeleteRound}
                loading={busy}
              >
                {getTranslation("BUTTON_DELETE") || "Delete"}
              </Button>
            )}
            {editable && (
              <Button onClick={handleMarkReady} loading={busy}>
                {getTranslation("BUTTON_SAVE_READY") || "Save as ready"}
              </Button>
            )}
          </Group>
        </Paper>

        {/* Notices for this round */}
        <Paper withBorder p="md" radius="md">
          <Title order={5} mb="sm">
            {getTranslation("LABEL_NOTICES") || "Notices"}
          </Title>
          {round.notices.length === 0 ? (
            <Text c="dimmed" size="sm">
              {getTranslation("MESSAGE_NO_NOTICES") || "No notices."}
            </Text>
          ) : (
            <Stack gap="xs">
              {round.notices.map((n) => (
                <Paper key={n.id} withBorder p="sm" radius="sm">
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <div>
                      <Text fw={600} size="sm">
                        {n.groupName} / {n.itemName}
                      </Text>
                      {n.description && (
                        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                          {n.description}
                        </Text>
                      )}
                      <Group gap="md" mt={4}>
                        {n.responsibleUserName && (
                          <Text size="xs" c="dimmed">
                            {getTranslation("LABEL_RESPONSIBLE_PERSON") ||
                              "Responsible"}
                            : {n.responsibleUserName}
                          </Text>
                        )}
                        {n.dueDate && (
                          <Text size="xs" c="dimmed">
                            {getTranslation("LABEL_DUE_DATE") || "Due"}:{" "}
                            {formatDate(n.dueDate)}
                          </Text>
                        )}
                        {n.fixed && (
                          <Text size="xs" c="green">
                            {getTranslation("LABEL_FIXED_DATE") || "Corrected"}:{" "}
                            {formatDate(n.fixed)}
                          </Text>
                        )}
                      </Group>
                    </div>
                    <Group gap={4} wrap="nowrap">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => openNotice(n.groupId, n)}
                        aria-label="edit-notice"
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      {round.status < 2 && canModify && (
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleDeleteNotice(n)}
                          aria-label="delete-notice"
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>
    );
  }

  // ── List view (default) ────────────────────────────────────────────────
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4} c="blue">
          {getTranslation("TITLE_MVR_MEASUREMENTS") || "MVR measurements"}
          {overview?.objectName ? ` - ${overview.objectName}` : ""}
        </Title>
        {canModify && (
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAddNew}
            loading={busy}
          >
            {getTranslation("BUTTON_ADD_NEW") || "Add new"}
          </Button>
        )}
      </Group>

      {overview && overview.rounds.length > 0 && (
        <Paper withBorder p="md" radius="md">
          <MvrChart
            points={overview.chartPoints}
            target={overview.target}
            averageLabel={`${getTranslation("LABEL_AVERAGE") || "Average"} ${
              overview.averagePercentage
            } %`}
          />
        </Paper>
      )}

      {!overview || overview.rounds.length === 0 ? (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          {getTranslation("MESSAGE_NO_ROUNDS") ||
            "No MVR measurements for this object yet."}
        </Alert>
      ) : (
        <Stack gap="xs">
          {overview.rounds.map((r) => (
            <Paper key={r.id} withBorder p="sm" radius="md">
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm">
                  <Text fw={600}>
                    {r.week} {r.creatorName} {r.percentage} %
                  </Text>
                  {r.status === 2 && (
                    <Badge color="green" variant="light">
                      {getTranslation("LABEL_READY") || "Ready"}
                    </Badge>
                  )}
                  {r.status === 1 && (
                    <Badge color="gray" variant="light">
                      {getTranslation("LABEL_DRAFT") || "Draft"}
                    </Badge>
                  )}
                  {r.noticeCount > 0 && (
                    <Badge
                      color="orange"
                      variant="light"
                      leftSection={<IconAlertTriangle size={12} />}
                    >
                      {r.noticeCount}
                    </Badge>
                  )}
                </Group>
                <Group gap={4} wrap="nowrap">
                  {r.status === 1 && (
                    <Anchor component="button" onClick={() => openRound(r.id)}>
                      {getTranslation("BUTTON_EDIT") || "Edit"}
                    </Anchor>
                  )}
                  {r.status === 2 && (
                    <Anchor component="button" onClick={() => openRound(r.id)}>
                      {getTranslation("BUTTON_VIEW") || "View"}
                    </Anchor>
                  )}
                  {r.status === 2 && canRelease && (
                    <ActionIcon
                      variant="subtle"
                      onClick={() => handleReleaseRound(r.id)}
                      aria-label="release-round"
                      title={getTranslation("BUTTON_RELEASE") || "Release"}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  )}
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

// ── Lightweight inline SVG chart (no external dependency) ──────────────────
interface MvrChartProps {
  points: { week: string | null; percentage: number }[];
  target: number;
  averageLabel: string;
}

function MvrChart({ points, target, averageLabel }: Readonly<MvrChartProps>) {
  const width = 720;
  const height = 260;
  const padding = { top: 20, right: 20, bottom: 40, left: 40 };
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;

  const maxY = 100;
  const xFor = (i: number) =>
    points.length <= 1
      ? padding.left + plotW / 2
      : padding.left + (i / (points.length - 1)) * plotW;
  const yFor = (v: number) =>
    padding.top + plotH - (Math.max(0, Math.min(maxY, v)) / maxY) * plotH;

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${xFor(i)},${yFor(p.percentage)}`)
    .join(" ");

  const targetY = yFor(target);
  const gridValues = [0, 20, 40, 60, 80, 100];

  return (
    <div style={{ overflowX: "auto" }}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label="MVR results chart"
      >
        {/* Y grid + labels */}
        {gridValues.map((v) => (
          <g key={v}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yFor(v)}
              y2={yFor(v)}
              stroke="#e9ecef"
            />
            <text x={4} y={yFor(v) + 4} fontSize={11} fill="#868e96">
              {v}
            </text>
          </g>
        ))}

        {/* Target line */}
        {target > 0 && (
          <line
            x1={padding.left}
            x2={width - padding.right}
            y1={targetY}
            y2={targetY}
            stroke="#fa5252"
            strokeDasharray="6 4"
          />
        )}

        {/* Result line */}
        <path d={linePath} fill="none" stroke="#009df0" strokeWidth={2} />

        {/* Points + X labels */}
        {points.map((p, i) => (
          <g key={`${p.week}-${i}`}>
            <circle cx={xFor(i)} cy={yFor(p.percentage)} r={4} fill="#009df0" />
            <text
              x={xFor(i)}
              y={height - padding.bottom + 20}
              fontSize={11}
              fill="#868e96"
              textAnchor="middle"
            >
              {p.week}
            </text>
          </g>
        ))}
      </svg>
      <Text ta="right" fw={600} mt="xs">
        {averageLabel}
      </Text>
    </div>
  );
}
